import {action, computed, makeObservable, observable, reaction} from 'mobx'
import Club from '../models/Club'
import {post} from '../common/requester'
import Member from "../models/Member";
import MemberQuery from "../models/MemberQuery";
import Person from '../models/Person'
import Company from "../models/Company";
import Team from '../models/Team'
import TeamQuery from '../models/TeamQuery'
import Schedule from "../models/Schedule";
import Training from "../models/Training";
import Participant from "../models/Participant";
import Attendance from '../models/Attendance';
import appStore from "./AppStore";
import Billing from "../models/Billing";
import BillingDetail from "../models/BillingDetail";
import Tournament from "../models/Tournament";
import Parent from "../models/Parent";
import GameMembersQuery from '../models/GameMembersQuery'
import Loc from '../models/Loc'
import Trainer from '../models/Trainer'
import CompanySearchStore from "./CompanySearchStore";
import { getMenu as getMenuData } from '../common/menu';
import Agreement, {AGREEMENT_CLUB1_OWNER, AGREEMENT_CLUB_MEMBER} from '../models/Agreement'

class ClubStore {
  loading = false;
  club = new Club();
  club_edit = new Company();
  member = new Member();
  memberQuery = new MemberQuery();
  members = [];
  branch = new Company();
  branches = [];
  team = new Team();
  teamQuery = new TeamQuery();
  teams = [];
  freeMembers = [];
  training = new Training();
  attendance = new Attendance();
  billing = new Billing(this);
  billing_types = [];
  billing_detail = new BillingDetail();
  tournament = new Tournament(this, 'manager');
  parent = new Parent(this, 'parent')
  child = new Parent(this, 'child')
  selectedMenu = ''
  freeMemberQuery = new GameMembersQuery()
  loc = new Loc()
  trainer = new Trainer()
  companySearch = new CompanySearchStore(this)
  agreement = new Agreement(this)
  agreements = []
  
  constructor() {
    makeObservable(this, {
      loading: observable,
      club: observable,
      club_edit: observable,
      member: observable,
      members: observable,
      memberQuery: observable,
      branch: observable,
      branches: observable,
      team: observable,
      teams: observable,
      teamQuery: observable,
      freeMembers: observable,
      training: observable,
      getClub: action,
      saveMember: action,
      getMembers: action,
      setMembers: action,
      resetMember: action,
      getMember: action,
      resetBranch: action,
      saveBranch: action,
      getBranches: action,
      setBranches: action,
      getBranch: action,
      saveTeam: action,
      resetTeam: action,
      getTeams: action,
      getTeamsTrainer: action,
      setTeams: action,
      getTeam: action,
      getFreeMembers: action,
      setFreeMembers: action,
      addTeamMembers: action,
      deleteTeamMember: action,
      setLoading: action,
      getTraining: action,
      setTraining: action,
      getMenu: computed,
      is_manager: computed,
      is_trainer: computed,
      is_parent: computed,
      is_member: computed,
      billing: observable,
      billing_types: observable,
      getBillingTypes: action,
      setBillingTypes: action,
      searchMembersForFilter: action,
      getBillingDetail: action,
      billing_detail: observable,
      tournament: observable,
      getNewbies: action,
      getLastThreeMonths: action,
      getMonthRange: action,
      selectedMenu: observable,
      setSelectedMenu: action,
      freeMemberQuery: observable,
      loc: observable,
      getTrainersForClub: action,
      parent: observable,
      child: observable,
      trainer: observable,
      sendGameMedia: action,
      informGame: action,
      getGameTeams: action,
      getGameTeamDetails: action,
      companySearch: observable,
      agreement: observable,
      agreements: observable,
      getAgreements: action,
      getAgreement: action,
      initAgreement: action,
      deleteAgreement: action,
      setActiveAgreement: action,
      setAgreements: action
    })

    reaction(()=>this.club.id, (new_id, old_id) => {
      this.memberQuery.setProperty('company_id', new_id)
      this.branch.setProperty('parent_id', new_id)
      this.attendance.query.setProperty('company_id', new_id)
      this.billing.query.setProperty('company_id', new_id)
      this.tournament.setProperty('company_id', new_id)
      this.tournament.game.setProperty('company_id', new_id)
      this.tournament.competition.setProperty('company_id', new_id)
      this.parent.setProperty('company_id', new_id)
      this.child.setProperty('company_id', new_id)
      this.loc.setProperty('company_id', new_id)
      this.trainer.setProperty('company_id', new_id)
      this.agreement.setProperty('company_id', new_id)
      this.agreement.setProperty('type', AGREEMENT_CLUB_MEMBER)
    })
  }

  setLoading(loading) {
    this.loading = loading;
  }
  
  async getClub(company_id) {
    const res = await post('company', {company_id}).catch((err) => console.error('--company error', err))
    if (res) {
      this.club.fromJson(res.company);
      let ch = []
      res.company.children?.map((child) => {
        const c = new Club();
        c.fromJson(child);
        ch.push(c)
      })
      if (ch) {
        this.club.setProperty('children', ch)
      }
      if (!this.member.id) {
        this.member.company_id = this.club.id;
      }
      this.getTeams()
    }
  }

  get is_manager() {
    return this.club.id && this.club.roles.some((role) => role.id === 'MGR' || role.id === 'OWN')
  }

  get is_trainer() {
    return this.club.id && this.club.roles.some((role) => role.id === 'TRN')
  }

  get is_parent() {
    return this.club.id && this.club.roles.some((role) => role.id === 'PRNT')
  }

  get is_member() {
    return this.club.id && this.club.roles.some((role) => role.id === 'MMBR')
  }

  get getMenu() {
  const menuData = getMenuData()
  const byRole = (role) => menuData.filter((item) => item.role === role)

  let items = []

  const hasCompanyContext =
    (appStore.user.companies.length > 1 && this.club.id === 0) ||
    this.club.id > 0

  if (hasCompanyContext) {
    items = [...items, ...byRole('company')]
  }

  if (this.club.id) {
    if (appStore.user.is_admin) items = [...items, ...byRole('admin')]
    if (this.is_manager) items = [...items, ...byRole('manager')]
    if (this.is_trainer) items = [...items, ...byRole('trainer')]
    if (this.is_parent) items = [...items, ...byRole('parent')]
    if (this.is_member) items = [...items, ...byRole('member')]
  }

  items = [
    ...items,
    ...(appStore.user.id > 0 ? byRole('common') : byRole('anonymous'))
  ]

  return items
}
  
  setSelectedMenu(menu) {
    this.selectedMenu = menu
  }
  
  async saveMember() {
    const isValid = this.member.validate();
    if (!isValid) {
      return Promise.reject(
          new Error('please_check_data')
      );
    }
    const photoUpload =
        this.member.photo_upload;

    // Save CompanyUser without the binary file.
    const payload = this.member.toJson();
    delete payload.photo_upload;
    const res = await post(
        'save_company_user',
        payload
    ).catch((err) => {
      console.error(
          '--member save error',
          err
      );
    });
    if (!res) {
      return Promise.reject(
          new Error('save_error')
      );
    }
    this.resetMember();
    this.member.fromJson(
        res.company_user
    );
    // Now member already has an ID.
    if (photoUpload) {
      this.member.setProperty(
          'photo_upload',
          photoUpload
      );

      await this.member.upload_avatar();
    }
    return this.member;
  }

  setMembers(members) {
    this.members = members;
  }
  
  resetMember() {
    this.member = new Member();
  }
  
  fetchMembers = async (append) => {
    this.setLoading(true)
    try {
      const response = await post('get_company_users', this.memberQuery).catch((err) => console.error('--members list error', err))

      if (response && response.result === 0) {
        let mm = []
        response.items.map((member) => {
          const m = new Member();
          m.fromJson(member);
          mm.push(m)
        })
        if (append) {
          this.setMembers([...this.members, ...mm])
        } else {
          this.setMembers(mm)
        }
        this.memberQuery.setProperty('count', response.count)
        this.memberQuery.setProperty('page_count', response.page_count)
        this.memberQuery.setProperty('page_number', response.page_number)
      }
      return Promise.resolve(response)
    } catch (err) {
      console.error('--members list error', err)
      return Promise.reject(err)
    } finally {
      this.setLoading(false)
    }
  }

  async getMembers() {
    this.memberQuery.setProperty('page_number', 1)
    return this.fetchMembers(false)
  }
  
  async getMember(member_id) {
    const res = await post(
        "get_company_users",
        {
            ...this.memberQuery,
            company_user_id: member_id,
        }
    ).catch((err) =>
        console.error(
            "--member get error",
            err
        )
    );


    const data =
        res?.items?.[0];


    if (!data) {
        return null;
    }


    this.resetMember();

    this.member.fromJson(
        data
    );


    /* =====================================================
       MEMBER
       ===================================================== */

    if (data.role_id === "MMBR") {

        const mother =
            new Person("mother");

        if (data.mother) {
            mother.fromJson(
                data.mother
            );
        }


        const father =
            new Person("father");

        if (data.father) {
            father.fromJson(
                data.father
            );
        }


        this.member.setProperty(
            "mother",
            mother
        );

        this.member.setProperty(
            "father",
            father
        );
    }


    /* =====================================================
       PARENT
       ===================================================== */

    if (data.role_id === "PRNT") {

        const children =
            Array.isArray(data.children)
                ? data.children
                    .map((child) => {
                        const id =
                            child.id ??
                            child.company_user_id;


                        if (!id) {
                            return null;
                        }


                        return {
                            ...child,

                            id,

                            company_user_id:
                                child.company_user_id ??
                                id,
                        };
                    })
                    .filter(Boolean)
                : [];


        this.member.setProperty(
            "children",
            children
        );
    }


    return this.member;
}

  resetBranch() {
    this.branch = new Company();
  }

  async saveBranch() {
    if (!this.branch.parent_id) {
      this.branch.setProperty('parent_id', this.club.id)
    }
    const isValid = this.branch.validate();
    console.log('--branch save', isValid)
    if (isValid) {
      const res = await post('save_branch', this.branch)
        .catch((err) => console.error('--branch save error', err))
      if (res) {
        this.branch.fromJson(res.branch);
        return Promise.resolve(this.branch)
      } else {
        return Promise.reject()
      }
    }
    return Promise.reject(new Error('please_check_data'))
  }

  setBranches(branches) {
    this.branches = branches;
  }

  async getBranches() {
    const res = await post('branches', {company_id: this.club.id}).catch((err) => console.error('--branches list error', err))
    if (res) {
      this.setBranches(res.branches)
    }
  }

  async getBranch(branch_id) {
    const res = await post('branch', {branch_id}).catch((err) => console.error('--branch get error', err))
    if (res) {
      this.branch.fromJson(res.branch);
    }
  }
  
  async saveTeam() {
    if (!this.team.company_id) {
      this.team.setProperty('company_id', this.club.id)
    }
    const isValid = this.team.validate();
    if (isValid) {
      const res = await post('company/team/update', this.team)
        .catch((err) => console.error('--team save error', err))
      if (res) {
        this.team.fromJson(res.team);
        return Promise.resolve(this.team)
      } else {
        return Promise.reject()
      }
    }
    return Promise.reject(new Error('please_check_data'))
  }
  
  resetTeam() {
    this.team = new Team();
  }
  setTeams(teams) {
    this.teams = teams;
  }
  async getTeams() {
    if(!this.teamQuery.parent_id) {
      this.teamQuery.setProperty('parent_id', this.club.id)
    }
    const res = await post('company/team/list', this.teamQuery).catch((err) => console.error('--teams list error', err))
    if (res) {
      this.setTeams(res.teams)
    }
  }

  async getTeamsTrainer() {
    if(!this.teamQuery.parent_id) {
      this.teamQuery.setProperty('parent_id', this.club.id)
    }
    const res = await post('trainer/team/list', this.teamQuery).catch((err) => console.error('--teams list error', err))
    if (res) {
      this.setTeams(res.teams)
    }
  }
  
  async getTeam(team_id) {
    const res = await post('company/team/get', {team_id}).catch((err) => console.error('--team error', err))
    if (res) {
      this.team.fromJson(res.team);
      let schedule = new Schedule()
      if (res.team.schedule) {
        res.team.schedule.map((day) => {
          let dd = schedule.days.find(d => d.name === day.name)
          if (dd) {
            dd.setProperty('is_on', day.is_on)
            dd.setProperty('time_from', day.time_from)
            dd.setProperty('time_to', day.time_to)
          }
        })
      }
      this.team.setProperty('schedule', schedule)
      this.training.setProperty('team_id', this.team.id)
    }
  }
  
  setFreeMembers(members) {
    this.freeMembers = members;
  }
  
  async getFreeMembers() {
    if(!(this.club.id && this.team.id)) {
      return
    }
    const res = await post('company/team/free_members', {company_id: this.club.id, team_id: this.team.id, title: this.freeMemberQuery.title, year_born: this.freeMemberQuery.year_born}).catch((err) => console.error('--free members error', err))
    if (res) {
      this.setFreeMembers(res.members)
    }
  }
  async addTeamMembers(user_ids) {
    const res = await post('company/team/member/edit', {team_id: this.team.id, company_user_ids: user_ids}).catch((err) => console.error('--add team member error', err))
    if (res) {
      this.team.fromJson(res.team);
      return this.getFreeMembers()
    } else {
      return Promise.reject()
    }
  }
  async deleteTeamMember(team_member_id) {
    const res = await post('company/team/member/delete', {
      team_member_id
    }).catch((err) => console.error('--delete team member error', err))
    if (res) {
      this.team.members = this.team.members.filter(m => m.id !== team_member_id)
      this.team.fromJson(res.team);
      return this.getFreeMembers()
    } else {
      return Promise.reject()
    }
  }
  setTraining(training) {
    this.training = training;
    let participants = [];
    this.training.participants.map((pp) => {
      let p = new Participant();
      p.fromJson(pp);
      participants.push(p)
    })
    this.training.participants = participants;
  }

  async getTraining(class_id) {
    if(!(this.club.id && this.team.id)) {
      return
    }
    const res = await post('company/team/class/get', {class_id}).catch((err) => console.error('--class error', err))
    if (res) {
      this.setTraining(res.class)
    }
  }

  async archiveUser(company_user_id) {
    const res = await post('company/user/archive', {company_user_id}).catch((err) => console.error('--archive user error', err))
    if (res) {
      return this.getMembers()
    }
  }

  async unArchiveUser(company_user_id) {
    const res = await post('company/user/unarchive', {company_user_id}).catch((err) => console.error('--unarchive user error', err))
    if (res) {
      return this.getMembers()
    }
  }

  async createTraining() {
    if(!(this.club.id && this.team.id)) {
      return
    }
    const res = await post('company/team/class/create', {
      team_id: this.team.id
    }).catch((err) => console.error('--create class error', err))
    if (res) {
      this.setTraining(res.class)
      return Promise.resolve(res.class)
    } else {
      return Promise.reject()
    }
  }

  setBillingTypes(types) {
    this.billing_types = types;
  }

  async getBillingTypes() {
    const res = await post('billing_types', {}).catch((err) => console.error('--billing types error', err))
    if (res) {
      this.setBillingTypes(res.billing_types)
    }
  }

async searchMembersForFilter({
    title = null,
    role_id = null,
    company_user_id = null,
    company_id = null,

    for_parent = false,
    is_mother = false,
    is_father = false,
    parent_id = null,
} = {}) {

    const targetCompanyId =
        company_id ||
        this.club.id;


    if (!targetCompanyId) {
        return [];
    }


    try {
        const res = await post(
            "company/user/search",
            {
                company_id:
                    targetCompanyId,

                role_id,

                title:
                    typeof title === "string"
                        ? title.trim()
                        : title,

                company_user_id,

                for_parent:
                    Boolean(for_parent),

                is_mother:
                    Boolean(is_mother),

                is_father:
                    Boolean(is_father),

                parent_id:
                    parent_id || null,
            }
        );


        const members =
            Array.isArray(res?.members)
                ? res.members
                : [];


        /*
         * Keep one consistent frontend format.
         *
         * New backend returns both id and company_user_id,
         * but normalization also keeps compatibility with
         * an older API response.
         */
        return members
            .map((member) => {
                const id =
                    member.id ??
                    member.company_user_id;


                if (!id) {
                    return null;
                }


                return {
                    ...member,

                    id,

                    company_user_id:
                        member.company_user_id ??
                        id,
                };
            })
            .filter(Boolean);

    } catch (error) {
        console.error(
            "--search members error",
            error
        );

        return [];
    }
}

  async getBillingDetail(billing_id) {
    const res = await post(
        'manager/billing/detail',
        {
          billing_id: billing_id,
          company_id: this.club.id
        }
    ).catch((err) => console.error('--billing detail error', err))

    if (res?.billing) {
      this.billing_detail.fromJson(res.billing)
    }
  }

  async getNewbies() {
    return post('team/newbies', {company_id: this.club.id})
  }
  
  getLastThreeMonths() {
    const today = new Date();
    const months = [];

    let year = today.getFullYear();
    let month = today.getMonth(); // start from CURRENT month

    for (let i = 0; i < 3; i++) {
        if (month < 0) {
            month += 12;
            year -= 1;
        }

        const monthName = new Date(year, month, 1).toLocaleString("en-US", {
            month: "long",
        });

        months.push({
            year,
            monthIndex: month,
            monthNumber: month + 1,
            monthName,
        });

        month -= 1;
    }

    return months.reverse();
}
  
  getMonthRange(year, monthIndex) {
    const first = new Date(year, monthIndex, 1);
    const last = new Date(year, monthIndex + 1, 0);
    return {first, last};
  }
  
  async getTrainersForClub() {
    const res = await post('company/trainers', {company_id: this.club.id}).catch((err) => console.error('--trainers error', err))
    if (res) {
      this.club.setProperty('trainers', res.trainers)
    }
  }

  async sendGameMedia(game_id) {
    await post('game/send_game_media', {
      company_id: this.club.id, game_id: game_id
    }).catch(error => console.log(error))
  }

  async informGame(game_id) {
    return post('game/inform_game', {company_id: this.club.id, game_id: game_id})
  }

  async getGameTeams() {
    const res = await post('game/get_game_teams', {company_id: this.club.id}).catch((err) => console.error('--game teams error', err))
    if (res) {
      this.club.setProperty('game_teams', res.game_teams)
    }
  }

  async getGameTeamDetails(game_team_id) {
    const ret = await post('game/get_game_team_details', {
      company_id: this.club.id,
      game_team_id,
    }).catch(error => console.error(error))

    if (ret && ret.result === 0) {
      this.club.setProperty('game_team_details', ret.game_team)
    }
  }
  
  async getAgreements(params) {
    const ret = await post("agreement/list", params).catch((err) => console.log(err))
    this.setAgreements(ret.items.map((x) => new Agreement().setFromJson(x)))
  }
  
  async getAgreement(id) {
    const ret = await post("agreement/get", {id}).catch((err) => console.log(err))
    
    const a = new Agreement();
    a.setFromJson(ret);
    this.agreement = a;
  }
  
  async deleteAgreement(id) {
    const ret = await post("agreement/delete", {id}).then(() => {
      this.agreements = (this.agreements || []).filter((x) => x.id !== id);
    }).catch((err) => console.log(err))
  }
  
  setAgreements(agr) {
    this.agreements = agr
  }
  
  async setActiveAgreement(id) {
    const ret = await post("agreement/set_active", {id}).catch((err) => console.log(err))
  
    this.setAgreements((this.agreements || []).map((x) => {
        x.setProperty("is_active", x.id === id);
        return x;
      })
    )
    return ret;
  }
  
  initAgreement() {
    const a = new Agreement();
    
    a.setProperty("id", null);
    a.setProperty("company_id", this.club?.id);
    a.setProperty("type", AGREEMENT_CLUB_MEMBER);
    a.setProperty("title", "");
    a.setProperty("text", "");
    a.setProperty("version", new Date().toISOString().slice(0, 10));
    a.setProperty("is_active", true);
    
    this.agreement = a;
    console.log('agreement', this.agreement)
    return a;
  }
}

const clubStore = new ClubStore();
export default clubStore;